/*
 * Café Arona analytics: the single module that talks to Google Analytics 4.
 *
 * Interface, all declarative:
 *   - the JSON context from snippets/google-analytics.liquid (ID, shared properties, page event);
 *   - data-analytics-event + data-analytics-params on anything clickable;
 *   - data-analytics-fire + data-analytics-params on server-rendered goal markers.
 * CTA, email, phone, social, language and commerce interactions need no markup.
 *
 * Requires an explicit yes from the visitor. Shopify allows analytics by default outside
 * configured banner regions, so analyticsProcessingAllowed() is not strict enough for
 * Québec. If the privacy API cannot load, Google is never contacted.
 */
(function () {
  const contextNode = document.querySelector('[data-analytics-context]');
  if (!contextNode) return;

  const context = JSON.parse(contextNode.content);
  const id = context.id;

  const SOCIAL_HOSTS = ['instagram', 'facebook', 'youtube', 'linkedin', 'tiktok'];
  const LOOKS_PERSONAL = /@|\d[\d\s().-]{6,}/;
  // Anything else in an address (search terms, form flags) never reaches Google.
  const SAFE_QUERY = /^(utm_[a-z]+|gclid|page|sort_by)$/;
  const SUCCESS_FLAGS = ['contact_posted', 'customer_posted'];
  const disableFlag = `ga-disable-${id}`;

  let tagLoaded = false;
  let collecting = false;

  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  function track(name, params) {
    if (collecting) gtag('event', name, params || {});
  }

  function parseParams(raw) {
    try {
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  }

  function safeUrl(address) {
    if (!address) return '';
    const url = new URL(address);
    [...url.searchParams.keys()].forEach((key) => {
      if (!SAFE_QUERY.test(key)) url.searchParams.delete(key);
    });
    return url.href;
  }

  /** Search titles repeat the visitor's words, so they are replaced by the template name. */
  function safeTitle() {
    return new URL(location.href).searchParams.has('q') ? context.globals.page_template : document.title;
  }

  function availabilityState(params) {
    const comingSoon = document.querySelector('[data-pdp][data-coming-soon]')?.dataset.comingSoon === 'true';
    if (comingSoon) return 'coming_soon';
    return params.available === false ? 'sold_out' : 'available';
  }

  /** Copies the page event, adding derived properties and removing anything personal. */
  function preparePageEvent(event) {
    const { available, ...params } = event.params;
    if (event.name === 'view_item') {
      return { name: event.name, params: { ...params, availability_state: availabilityState(event.params) } };
    }
    if (event.name === 'search' && LOOKS_PERSONAL.test(params.search_term)) {
      const { search_term, ...safe } = params;
      return { name: event.name, params: safe };
    }
    return { name: event.name, params };
  }

  function firePageEvents() {
    if (context.pageEvent) {
      const event = preparePageEvent(context.pageEvent);
      track(event.name, event.params);
    }
    const markers = document.querySelectorAll('[data-analytics-fire]');
    // Shopify marks every form of a type successful, so one signup can render two markers.
    const fired = new Set();
    markers.forEach((marker) => {
      if (fired.has(marker.dataset.analyticsFire)) return;
      fired.add(marker.dataset.analyticsFire);
      track(marker.dataset.analyticsFire, parseParams(marker.dataset.analyticsParams));
    });
    if (markers.length) forgetSuccessFlags();
  }

  /** Reloading the page then shows the plain form, so a goal cannot count twice. */
  function forgetSuccessFlags() {
    const url = new URL(location.href);
    SUCCESS_FLAGS.forEach((flag) => url.searchParams.delete(flag));
    history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function loadTag() {
    tagLoaded = true;
    gtag('js', new Date());
    gtag('set', {
      ...context.globals,
      page_location: safeUrl(location.href),
      page_referrer: safeUrl(document.referrer),
      page_title: safeTitle(),
    });
    gtag('config', id, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false });
    const tag = document.createElement('script');
    tag.async = true;
    tag.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(tag);
    track('page_view');
    firePageEvents();
  }

  /** Expires Google's cookies on this host and its parent domains, leaving Shopify's alone. */
  function deleteGoogleCookies() {
    const labels = location.hostname.split('.');
    const domains = [''];
    for (let start = 0; start < labels.length - 1; start += 1) {
      domains.push(`; domain=.${labels.slice(start).join('.')}`);
    }
    document.cookie.split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter((name) => name === '_ga' || name.startsWith('_ga_'))
      .forEach((name) => {
        domains.forEach((domain) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
        });
      });
  }

  function applyConsent() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return;
    const allowed = privacy.currentVisitorConsent().analytics === 'yes';
    window[disableFlag] = !allowed;
    collecting = allowed;
    if (allowed && !tagLoaded) loadTag();
    if (!allowed) deleteGoogleCookies();
  }

  function trackLink(link) {
    const href = link.href || '';
    if (href.startsWith('mailto:')) return track('contact_click', { contact_method: 'email' });
    if (href.startsWith('tel:')) return track('contact_click', { contact_method: 'phone' });
    const network = SOCIAL_HOSTS.find((host) => (link.hostname || '').includes(host));
    if (network) track('social_click', { social_network: network });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!collecting || !target?.closest) return;
    const declared = target.closest('[data-analytics-event]');
    if (declared) track(declared.dataset.analyticsEvent, parseParams(declared.dataset.analyticsParams));
    const cta = target.closest('a.cta-button');
    if (cta) {
      track('cta_click', {
        cta_label: cta.textContent.trim(),
        cta_location: cta.closest('header, footer, main')?.tagName.toLowerCase() ?? 'other',
        cta_url: cta.pathname,
      });
    }
    const link = target.closest('a[href]');
    if (link) trackLink(link);
  });

  document.addEventListener('submit', (event) => {
    if (!collecting) return;
    const form = event.target;
    if (form.id?.startsWith('localization-form') && event.submitter?.name === 'locale_code') {
      track('language_switch', { to_language: event.submitter.value });
      return;
    }
    if (form.id === 'pdp-form' && context.pageEvent?.name === 'view_item') {
      const { currency, value, items } = context.pageEvent.params;
      const variant = form.querySelector('[name="id"]')?.value;
      const quantity = Number(form.querySelector('[name="quantity"]')?.value) || 1;
      const price = context.variantPrices?.[variant] ?? value;
      track('add_to_cart', {
        currency,
        value: price * quantity,
        items: items.map((item) => ({ ...item, item_variant: variant, price, quantity })),
      });
      return;
    }
    if (form.querySelector?.('input[name="checkout"]')) track('begin_checkout', { currency: context.currency });
  });

  document.addEventListener('visitorConsentCollected', applyConsent);
  window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
    if (!error) applyConsent();
  });
})();
